/**
 * Wholesale / Trade Program — application form tagging.
 *
 * The application posts through Shopify's native `{% form 'customer' %}`, which
 * creates a real customer record on the store. A hidden `contact[tags]` field
 * carries the tags that record is created with, so applications are findable in
 * Admin → Customers by tag instead of living only in an inbox.
 *
 * The hidden field ships with a static base tag, so an applicant is tagged
 * correctly even with JavaScript disabled. This module only ENRICHES it on
 * submit, rebuilding the value from scratch each time, adding what the operator
 * chose in the form:
 *
 *   wholesale-applicant            always, from the markup
 *   type-wedding-planner           from the business-type select
 *   sample-requested               if the sample box is ticked
 *
 * Tags are deliberately "-applicant"/"-pending" style. The partner tags that
 * actually unlock pricing (`wholesale`, `trade`, `venue-partner` — see
 * `snippets/wholesale-customer-status.liquid`) are never settable from the
 * storefront; an operator applies those by hand after reviewing the business.
 * Keep it that way: this field is user-controlled input.
 *
 * Added 2026-08-30.
 */

/** Forms already wired up, so repeated init() calls don't double-bind. */
const bound = new WeakSet();

/**
 * Shopify accepts a comma-separated string for `contact[tags]`.
 *
 * @param {string} value
 * @returns {string} a lowercase, hyphenated tag fragment, or '' if unusable
 */
const toTag = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

/**
 * @param {HTMLFormElement} form
 */
const syncTags = (form) => {
  const tagsField = form.querySelector('[data-wholesale-tags]');
  if (!(tagsField instanceof HTMLInputElement)) return;

  // The base tag is read from the data attribute, which nothing ever writes to.
  //
  // It is deliberately NOT read from `value` or `defaultValue`: on this theme
  // both track each other, so seeding from either accumulated tags across
  // repeat submits — someone who picked the wrong business type, corrected it
  // and submitted again ended up tagged with both. Rebuilding from an immutable
  // source each time keeps the tags a description of the CURRENT form state.
  const tags = new Set(
    String(tagsField.dataset.wholesaleTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
  );

  const businessType = form.querySelector('[data-wholesale-business-type]');
  if (businessType instanceof HTMLSelectElement && businessType.value) {
    const tag = toTag(businessType.value);
    if (tag) tags.add(`type-${tag}`);
  }

  const sample = form.querySelector('[data-wholesale-sample]');
  if (sample instanceof HTMLInputElement && sample.checked) {
    tags.add('sample-requested');
  }

  tagsField.value = Array.from(tags).join(', ');
};

const init = () => {
  for (const form of document.querySelectorAll('[data-wholesale-form]')) {
    if (bound.has(form)) continue;
    bound.add(form);

    form.addEventListener('submit', () => syncTags(form));
  }
};

// Modules are deferred, so the document is normally parsed by now. The guard
// covers the case where this is loaded earlier than expected.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Re-bind newly rendered sections in the theme editor.
document.addEventListener('shopify:section:load', init);
